// Encapsulates all the Firebase interaction logic
// Provides all the data and functions using context

import {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  createContext,
  useContext,
} from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import {
  collection,
  collectionGroup,
  doc,
  documentId,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import {
  auth,
  db,
  tournamentScheduleSourceCheck,
  userSetOrganization,
} from '../utils/firebase';
import { imageUrls, userImagePaths } from '../utils/image';
import { updateAppBadge } from '../utils/badge';
import { refreshTokenIfGranted } from '../utils/notifications';
import { getMatchMomentUTC } from '../utils/misc';
import { readLastUid, writeLastUid } from '../utils/lastUid';
import { logFirebaseError, setErrorContext } from '../utils/errorLog';
import moment from 'moment-timezone';
import { UsersRoles, GameTypes, ParentStatuses } from '../utils/enums';
import { toast } from 'react-toastify';

const FirebaseContext = createContext();

export const useFirebase = () => useContext(FirebaseContext);

const snapToArr = (snapshot) => {
  let arr = [];
  snapshot.forEach((item) =>
    arr.push({
      id: item.id,
      ...item.data(),
    }),
  );
  return arr;
};

// Raw Firestore user doc → the shape the app consumes (date strings → moments,
// image filename → Storage paths). Shared by the users-collection listener and
// the current-user listener.
export const normalizeUserDoc = (id, data) => {
  const user = { id, ...data, ...userImagePaths(id, data.image) };

  if (user.birthDate) user.birthDate = moment(user.birthDate);
  if (user.card?.issueDate) user.card.issueDate = moment(user.card.issueDate);
  if (user.card?.expireDate)
    user.card.expireDate = moment(user.card.expireDate);

  return user;
};

// Raw Firestore team doc → the shape the app consumes. A directory row
// carries the same fields verbatim, so one normalizer serves all three sources.
export const normalizeTeamDoc = (team) => ({
  ...team,
  ...imageUrls('teams', team.id, team.image),
});

// The subcollections of a tournament, read only while a mounted consumer asks
// for one. `<part>Loaded` tells "not read yet" from "read, and empty";
// `<part>Synced` says the server confirmed it, which any conclusion drawn from
// an empty part needs. `<part>Failed` marks a refused read, which never syncs.
const tournamentParts = [
  'fields',
  'games',
  'teams',
  'standings',
  'managers',
  'checkIn',
  'info',
  'refereeRates',
  'referees',
];

// Readable only by a signed-in caller — the rules refuse them to a visitor
const privateTournamentParts = new Set(['managers', 'checkIn']);
// Referee pay and the whole roster: only admins and referee assigners read them
const refereeManagerParts = new Set(['refereeRates', 'referees']);

// For the failure toast, which reads "Failed to load tournament <label>"
const partLabels = {
  fields: 'fields',
  games: 'games',
  teams: 'teams',
  standings: 'standings',
  managers: 'managers',
  checkIn: 'check-in',
  info: 'info',
  refereeRates: 'referee rates',
  referees: 'referee roster',
};

// The one part that is not a plain array of documents: check-in times arrive as
// Timestamps and every consumer wants moments
const readTournamentPart = (part, snapshot) =>
  part === 'checkIn'
    ? snapToArr(snapshot).map((entry) => ({
        ...entry,
        time:
          entry.time instanceof Timestamp ? moment(entry.time.toDate()) : null,
      }))
    : snapToArr(snapshot);

const emptyTournParts = Object.freeze(
  Object.fromEntries(
    tournamentParts.flatMap((part) => [
      [part, Object.freeze([])],
      [`${part}Loaded`, false],
      [`${part}Synced`, false],
      [`${part}Failed`, false],
    ]),
  ),
);

// Raw Firestore tournament doc → the shape the app consumes. The subcollection
// slots start empty and are filled in by the part registry below. Exported
// because the home page reads tournaments through its own paged query and has
// to end up with documents indistinguishable from the ones the provider
// publishes.
export const normalizeTournamentDoc = (tourn) => ({
  ...tourn,
  startDate: getMatchMomentUTC({
    date: tourn.startDate,
    time: '00:00',
    timezone: moment.tz.guess(),
  }),
  endDate: getMatchMomentUTC({
    date: tourn.endDate,
    time: '23:59',
    timezone: moment.tz.guess(),
  }),
  ...imageUrls('tournaments', tourn.id, tourn.image),
  ...emptyTournParts,
});

// Entity registry: on-demand by-id reads for a record outside every other
// subscription. `in` takes at most 30 ids, and the client caps concurrent
// listeners at 100, so ids are packed into chunks instead of one listener each.
const ENTITY_CHUNK_SIZE = 30;
// How many recently-released ids stay subscribed so a back-navigation is free
const ENTITY_COLD_LRU_SIZE = 60;
// Chunks reshuffle (paying the resume-token cost) only once fragmentation
// reaches this ratio — not on every id added or released
const ENTITY_COMPACT_RATIO = 2;

const entityCollectionName = {
  users: 'users',
  teams: 'teams',
  tournaments: 'tournaments',
};

// `active: false` is how a team/tournament is deleted (see the `onDelete`
// handlers on their cards) — the full subscriptions already filter it out, so
// an on-demand lookup has to agree, or a deleted record would come back to
// life for anyone who still has the link.
const entityActiveOnly = { teams: true, tournaments: true };

const normalizeEntityDoc = (kind, id, data) => {
  if (kind === 'users') return normalizeUserDoc(id, data);
  if (kind === 'tournaments') return normalizeTournamentDoc({ id, ...data });
  return normalizeTeamDoc({ id, ...data });
};

const makeEntityBucket = () => ({
  chunks: new Map(), // chunkId -> { ids: Set<id>, unsub: fn|null }
  idToChunk: new Map(), // id -> chunkId
  refCount: new Map(), // id -> live-holder count
  zeroSet: new Set(), // ids released this tick, awaiting the flush microtask
  coldSet: new Set(), // released ids kept subscribed, free for back-nav
  coldQueue: [], // coldSet ids, oldest first; stale entries are skipped
  nextChunkId: 0,
  flushScheduled: false,
  // org (for 'users') or uid (for 'teams'/'tournaments') this bucket is valid
  // for — see ensureEntityBucketFresh
  stamp: undefined,
});

export default function FirebaseContextProvider({ children }) {
  // Seeded from the previous session so the listeners below can start at mount
  // instead of waiting out the network round trip Firebase Auth makes before
  // onAuthStateChanged fires. See utils/lastUid.
  const [uid, setUid] = useState(readLastUid);
  // True while `uid` is only a guess. onAuthStateChanged clears it, whether or
  // not the guess turned out right.
  const [isUidOptimistic, setIsUidOptimistic] = useState(() => !!readLastUid());
  // Which identity the collection listeners below were built for, so auth
  // confirming the guess is told apart from a real account change.
  const listenersUid = useRef(uid);
  // Same idea for the organization: tells a real switch apart from the effect
  // simply re-running, so only a switch blanks the data it invalidates.
  const listenersOrg = useRef(null);

  // The current user's own document, loaded on its own so that signing in does
  // not have to wait for the whole users collection. This is the app-ready gate.
  const [selfDoc, setSelfDoc] = useState(null);
  // null until the tourStatuses subcollection has been read at least once —
  // kept apart from the doc so the two listeners cannot race each other
  const [selfTourStatuses, setSelfTourStatuses] = useState(null);
  const [isSelfLoaded, setIsSelfLoaded] = useState(false);
  const [isAuthResolved, setIsAuthResolved] = useState(false);

  // Two tiers of readiness. `*Loaded` means the data is available — it may have
  // come straight from the IndexedDB cache, which is what lets the app render
  // in ~0.5s instead of waiting ~2.5s for the Firestore handshake. `*Synced`
  // means the server confirmed it, and is required before concluding that a
  // record does not exist: the cache can legitimately be incomplete.
  const [isSelfSynced, setIsSelfSynced] = useState(false);
  // The organization as the server last stated it, or null while it has not
  // stated one. Kept apart from `isSelfSynced` because that flag is also opened
  // by the self-document error handler so the app cannot hang behind a spinner
  // — which is exactly the case where nothing was confirmed at all.
  const [syncedOrg, setSyncedOrg] = useState(null);
  const [isTeamsSynced, setIsTeamsSynced] = useState(false);

  // The team documents of whichever tournaments somebody currently has open,
  // read by id — there is no organization-wide list any more. Surfaced only
  // through `teamsById`; a screen that wants the organization's teams as rows
  // reads the directory instead (`useTeamsDirectory`).
  const [teams, setTeams] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  // Subcollections of the tournaments somebody is currently looking at, kept
  // apart from the root documents on purpose: a part snapshot can land before
  // the root query has published its document, and patching an array by id
  // would drop that patch with no second chance — the listener does not fire
  // again for an unchanged subcollection. See the part registry below.
  const [tournParts, setTournParts] = useState(() => new Map());
  // The two halves of the chat surface, kept apart because they arrive from
  // two listeners: the chats themselves, and this member's read watermark in
  // each of them (chatId -> member document). Joined below.
  const [chatDocs, setChatDocs] = useState([]);
  const [chatMembers, setChatMembers] = useState(() => new Map());
  // The ids of the threads on screen, one entry per thread, whose new messages
  // are no news. A ref: it is only read when the chats change, so setting it
  // re-renders nothing
  const viewedChatRef = useRef([]);
  const [parentsByPlayerId, setParentsByPlayerId] = useState(new Map());
  // The organizations the user belongs to, read one by one — see the effect below
  const [orgsById, setOrgsById] = useState(new Map());
  // Entity registry: records fetched on demand for a consumer that asked for
  // one by id, merged into usersById/teamsById/tournamentsById below. See the
  // registry section further down.
  const [onDemandUsers, setOnDemandUsers] = useState(() => new Map());
  const [onDemandTeams, setOnDemandTeams] = useState(() => new Map());
  const [onDemandTournaments, setOnDemandTournaments] = useState(
    () => new Map(),
  );
  // Per-id: 'pending' | 'present' | 'missing'. A server-confirmed absence is
  // the only thing allowed to conclude 'missing' — see resolveEntityStatus.
  const [userStatusById, setUserStatusById] = useState(() => new Map());
  const [teamStatusById, setTeamStatusById] = useState(() => new Map());
  const [tournamentStatusById, setTournamentStatusById] = useState(
    () => new Map(),
  );

  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [userCreated, setUserCreated] = useState(null);

  const [isTeamsLoaded, setIsTeamsLoaded] = useState(false);
  // Which set of team ids isTeamsLoaded/isTeamsSynced were raised for,
  // compared against `openTeamIdsKey` before either is handed out
  const [teamsKey, setTeamsKey] = useState('');
  const [isTournamentsLoaded, setIsTournamentsLoaded] = useState(false);
  const [isChatListLoaded, setIsChatListLoaded] = useState(false);
  const [isChatListSynced, setIsChatListSynced] = useState(false);
  const [isChatMembersLoaded, setIsChatMembersLoaded] = useState(false);
  const [isChatMembersSynced, setIsChatMembersSynced] = useState(false);
  const [isParentsLoaded, setIsParentsLoaded] = useState(false);

  const self = useMemo(
    () =>
      selfDoc && {
        ...selfDoc,
        tourStatuses: selfTourStatuses ?? [],
        tourStatusesLoaded: selfTourStatuses !== null,
      },
    [selfDoc, selfTourStatuses],
  );

  // The whole profile comes from the caller's own document — there is no list
  // to fall back to any more, and there never needed to be one.
  const user = self ?? undefined;

  // A switch in flight: `{ organization, sent }`. `sent` flips once the write
  // has gone out — see `setOrganization` for why the two halves are told apart.
  const [orgSwitch, setOrgSwitch] = useState(null);
  // The promise handed to the caller of setOrganization, settled when the write
  // answers or when the identity changes out from under it
  const orgSwitchPromise = useRef(null);

  // Every root document carries `organizations` (membership); the user document
  // additionally carries `organization` — the one they are working in right now,
  // and the filter every query below is built on. Always one of `organizations`,
  // or null until an invite token grants the first one.
  const selfOrg = selfDoc?.organization ?? null;

  // Between the write and the snapshot echoing it, the value below runs ahead of
  // the self document: the rules already answer to the organization just
  // written, so the listeners may as well be built on it. Nothing else in the
  // app can tell the difference, and a self snapshot that never arrives cannot
  // strand the queries on an organization the rules no longer honour.
  const activeOrg = orgSwitch?.sent ? orgSwitch.organization : selfOrg;

  // The window this covers is the write itself: the listeners standing on the
  // organization being left have to be down before it lands
  const isOrgSwitching = !!orgSwitch && !orgSwitch.sent;

  // Keyed on the joined ids so the array identity survives self-doc snapshots
  // that only touched other fields — an image URL patch, for one
  const orgsKey = (selfDoc?.organizations ?? []).join('|');
  const myOrgs = useMemo(() => (orgsKey ? orgsKey.split('|') : []), [orgsKey]);

  // A visitor needs no organization; a signed-in user must not query before
  // their own document lands, or the filter goes out undefined
  const isOrgResolved = !uid || isSelfLoaded;

  // The gate above is not enough for a read the rules scope by organization.
  // The cache answers instantly, without rules and without waiting for auth, so
  // a self document that only came from IndexedDB makes `activeOrg` a guess —
  // while the rules resolve the tenant by reading users/{uid}.organization on
  // the server. Query on that guess and the refusal is the expected answer, not
  // a bug: either the cached value is behind the server's, or the listen went
  // out before Firebase Auth finished restoring the token. A server-confirmed
  // snapshot rules out both at once — it cannot arrive unauthenticated, since
  // reading one's own document already requires auth.
  //
  // Compared value by value rather than off a "the document synced once" flag: a
  // later cached snapshot can move `selfOrg` past what the server confirmed, and
  // a switch in flight has the server's word already — the callable returns only
  // after the write lands, which is what makes `orgSwitch.sent` proof in itself.
  const isOrgConfirmed = !uid || !!orgSwitch?.sent || syncedOrg === selfOrg;

  // The only call that names an organization — everywhere else the backend reads
  // it off the caller's document. It does not send the write itself: the rules
  // resolve the tenant by reading users/{uid}.organization, so the instant that
  // field changes, every listener still standing on the previous organization
  // stops satisfying them and the server rejects it — permission-denied on
  // queries that were legitimate when they opened, long before the new self
  // snapshot reaches React and takes them down. So the request only announces
  // the switch; the effect further down sends the write once those listeners are
  // gone. Rejects like the callable did, so the caller can spin and toast.
  const setOrganization = useCallback(
    (organization) =>
      new Promise((resolve, reject) => {
        orgSwitchPromise.current = { resolve, reject };
        setOrgSwitch({ organization, sent: false });
      }),
    [],
  );

  // Every user document in memory is one somebody asked for by id — there is
  // no organization-wide list any more. `self` carries tourStatuses, which a
  // registry entry for the same uid does not, so it is spread last.
  const usersById = useMemo(() => {
    const map = new Map(onDemandUsers);
    if (self) map.set(self.id, { ...map.get(self.id), ...self });
    return map;
  }, [self, onDemandUsers]);

  const teamsById = useMemo(() => {
    const map = new Map(teams.map((t) => [t.id, t]));
    for (const [id, doc] of onDemandTeams) if (!map.has(id)) map.set(id, doc);
    return map;
  }, [teams, onDemandTeams]);

  // Root document plus whatever of its subcollections is currently subscribed.
  // Consumers see one object, exactly as they did when the parts lived on the
  // document itself.
  const tournamentsWithParts = useMemo(
    () => tournaments.map((t) => ({ ...t, ...tournParts.get(t.id) })),
    [tournaments, tournParts],
  );

  const tournamentsById = useMemo(() => {
    const map = new Map(tournamentsWithParts.map((t) => [t.id, t]));
    for (const [id, doc] of onDemandTournaments) {
      if (!map.has(id)) map.set(id, { ...doc, ...tournParts.get(id) });
    }
    return map;
  }, [tournamentsWithParts, onDemandTournaments, tournParts]);

  // Backend-computed standings, keyed `${tournamentId}|${division}|${gameType}`.
  // The backend writes one doc per division holding pre-sorted ranking arrays
  // (`poolRanking` / `playoffRanking`) plus completion flags
  // (`poolComplete` / `playoffComplete`). Expand each doc into the per-gameType
  // shape the tables consume: { division, gameType, isComplete, rows } where
  // each row carries a synthesized `rank` (arrays are already rank-ordered).
  const standingsByKey = useMemo(() => {
    const map = new Map();
    const addEntry = (tId, division, gameType, ranking, isComplete) => {
      const rows = (ranking || []).map((r, i) => ({ ...r, rank: i + 1 }));
      map.set(`${tId}|${division}|${gameType}`, {
        division,
        gameType,
        isComplete: !!isComplete,
        rows,
      });
    };
    // Straight off the parts rather than the merged tournaments: renaming a
    // tournament must not re-derive every standings row on screen
    for (const [tId, parts] of tournParts) {
      for (const s of parts.standings || []) {
        addEntry(
          tId,
          s.division,
          GameTypes.POOLGAME,
          s.poolRanking,
          s.poolComplete,
        );
        addEntry(
          tId,
          s.division,
          GameTypes.PLAYOFF,
          s.playoffRanking,
          s.playoffComplete,
        );
      }
    }
    return map;
  }, [tournParts]);

  // Referee names come off the game documents, where the backend keeps
  // `refereeNames` index-aligned with `referees` and rewrites them when a
  // referee is renamed. This used to be the signed-out branch only, with a
  // signed-in caller filtering the organization-wide users list; that list is
  // gone, and one branch is better than two that drift apart in any case.
  // Every consumer reads `.name` and nothing else — GameBlock, GameListTable,
  // Scheduler, Brackets, useGameReport, GameBlockRefs. Somebody who needs the
  // referee's document asks for it by id (TrailBar, PageReferee).
  const refereesById = useMemo(() => {
    const map = new Map();
    for (const parts of tournParts.values()) {
      for (const game of parts.games || []) {
        (game.referees || []).forEach((id, i) => {
          if (map.has(id)) return;
          // An empty name means the user is gone. Left out rather than
          // entered blank, so it reads as the unknown referee it is
          const name = game.refereeNames?.[i];
          if (name) map.set(id, { id, name });
        });
      }
    }
    return map;
  }, [tournParts]);

  // Parent-player relationships come from a single collection group listener
  // (see below), so they no longer rebuild whenever the users list changes.
  const childrenByParentId = useMemo(() => {
    const map = new Map();
    for (const [playerId, parents] of parentsByPlayerId) {
      for (const { id: parentId, status } of parents) {
        if (!map.has(parentId)) map.set(parentId, []);
        map.get(parentId).push({ id: playerId, status });
      }
    }
    return map;
  }, [parentsByPlayerId]);

  const getParentStatus = useCallback(
    (playerId, parentId) => {
      const parents = parentsByPlayerId.get(playerId);
      if (!parents) return null;
      return parents.find((p) => p.id === parentId)?.status ?? null;
    },
    [parentsByPlayerId],
  );

  // Built from the team documents, not from `users`: a visitor reads no user
  // documents, and even a signed-in one only reads their own organization — so
  // walking `users` left the coaches of another organization's teams nameless
  // on a shared tournament. The backend keeps `coachIds` and `coachNames`
  // index-aligned on the team document.
  //
  // Off `teamsById`, never off the teams directory: this feeds GameBlock,
  // GameListTable, Brackets, Field, Scheduler and TeamCard — public screens
  // where a signed-out visitor cannot read the directory at all.
  const coachesByTeamId = useMemo(() => {
    const map = new Map();
    for (const team of teamsById.values()) {
      const ids = team.coachIds || [];
      const names = team.coachNames || [];
      map.set(
        team.id,
        ids.map((id, i) => ({ id, name: names[i] ?? '' })),
      );
    }
    return map;
  }, [teamsById]);

  // True once we know who the current user is — or that nobody is signed in.
  // This is the app-ready gate: it needs one document read, not the whole
  // users collection. A remembered uid is enough to open it — the cached
  // profile renders now and auth confirms it a moment later.
  const isUserResolved =
    (isAuthResolved || isUidOptimistic) && (!uid || isSelfLoaded);

  const userIsAdmin = user?.role === UsersRoles.ADMIN;
  const userIsCoach = user?.role === UsersRoles.COACH;
  const userIsPlayer = user?.role === UsersRoles.PLAYER;
  const userIsFieldManager = user?.role === UsersRoles.FIELDMANAGER;
  const userIsParent = user?.role === UsersRoles.PARENT;
  const userIsReferee = user?.role === UsersRoles.REFEREE;
  const userIsRefereeAssigner = user?.role === UsersRoles.REFEREE_ASSIGNER;
  // Referee rates, game referees and payouts: every gate on them asks this
  const userManagesReferees = userIsAdmin || userIsRefereeAssigner;

  // The teams the current user is bound to — their own for a coach or player,
  // and the union of their approved children's for a parent. It lived in
  // `useUserTeamIds`, which read every child out of the organization-wide users
  // list; the parent branch now registers those child documents by id (see the
  // effect next to `acquireEntities`), and this is where the readiness of that
  // chain is answered. Keyed on the joined ids rather than on the array, whose
  // identity moves with every parents snapshot (P7).
  const myChildIdsKey = useMemo(() => {
    if (!userIsParent || !uid) return '';
    return (childrenByParentId.get(uid) ?? [])
      .filter((child) => child.status === ParentStatuses.APPROVED)
      .map((child) => child.id)
      .sort()
      .join('|');
  }, [userIsParent, uid, childrenByParentId]);

  const myTeamIds = useMemo(() => {
    if (!user) return [];
    if (userIsCoach || userIsPlayer) return user.teams ?? [];
    if (!userIsParent || !myChildIdsKey) return [];

    const ids = new Set();
    for (const childId of myChildIdsKey.split('|')) {
      for (const teamId of usersById.get(childId)?.teams ?? []) ids.add(teamId);
    }
    return Array.from(ids);
  }, [user, userIsCoach, userIsPlayer, userIsParent, myChildIdsKey, usersById]);

  // The test every consumer builds on this is an allow-list, so a half-read
  // chain is indistinguishable from "belongs to no team" — hence a flag rather
  // than the ids alone. For a parent it takes three answers: the links, then
  // each child document (resolved, or confirmed absent).
  const isMyTeamIdsResolved =
    isUserResolved &&
    (!userIsParent ||
      (isParentsLoaded &&
        (!myChildIdsKey ||
          myChildIdsKey
            .split('|')
            .every(
              (id) => usersById.has(id) || userStatusById.get(id) === 'missing',
            ))));

  // `descriptor` carries what the console needs to make a refusal actionable —
  // which query went out, under which filters — plus the short human sentence
  // shown to the visitor. The raw Firebase text is deliberately not toasted:
  // "Missing or insufficient permissions." tells them nothing, and everything
  // technical goes to the console group instead.
  const reportListenerError = useCallback((err, listenerUid, descriptor) => {
    // Logged unconditionally: even a refusal not worth a toast is worth a line
    // while the rules are being written.
    logFirebaseError(err, descriptor);

    // A listener started for a remembered uid that auth then disowns fails with
    // permission-denied — the guess was wrong and the visitor is simply signed
    // out, which is nothing to tell them about. Compared against auth's own view
    // at failure time rather than a flag: the listener outlives the moment the
    // guess is settled, and only its own uid says whether the error is real.
    if (auth.currentUser?.uid !== listenerUid) return;

    // Some reads are opened speculatively for every signed-in user while only
    // certain roles may perform them. A refusal there is the expected answer,
    // not news for the visitor — it belongs in the console alone.
    if (descriptor?.silent) return;

    // `toastId` collapses the per-tournament subcollection listeners into one
    // message — a refused read fires once per tournament, and a stack of
    // identical toasts says nothing the first one did not
    toast.error(descriptor?.message ?? 'Something went wrong', {
      toastId: descriptor?.toastId,
    });
  }, []);

  // The token the rules actually evaluate. Read once per identity — nothing else
  // in the app looks at claims, so this exists purely so a denial can show
  // whether the client is carrying the claims the rules expect.
  const [tokenInfo, setTokenInfo] = useState(null);

  useEffect(() => {
    if (!uid) {
      setTokenInfo(null);
      return;
    }

    let cancelled = false;

    const read = async () => {
      try {
        const result = await auth.currentUser?.getIdTokenResult();
        if (cancelled || !result) return;
        setTokenInfo({
          claims: result.claims,
          issuedAt: result.issuedAtTime,
          expiresAt: result.expirationTime,
        });
      } catch {
        // Diagnostics only — a failure here must not disturb the app
      }
    };

    read();

    return () => {
      cancelled = true;
    };
    // `isAuthResolved` matters: on a warm start `uid` is the optimistic guess
    // and `auth.currentUser` is still null, so the read above yields nothing.
    // Without this dep a returning visitor — the case these diagnostics exist
    // for — would log every denial with empty claims.
  }, [uid, isEmailVerified, isAuthResolved]);

  // Keeps the logger's snapshot of the identity current, so an error landing at
  // any moment is described against the state as of that moment
  useEffect(() => {
    setErrorContext({
      uid,
      authUid: auth.currentUser?.uid ?? null,
      email: auth.currentUser?.email ?? null,
      emailVerified: isEmailVerified,
      isUidOptimistic,
      isAuthResolved,
      isSelfLoaded,
      isSelfSynced,
      isOrgResolved,
      isOrgConfirmed,
      syncedOrg,
      role: user?.role ?? null,
      activeOrg,
      myOrgs,
      teams: user?.teams ?? [],
      claims: tokenInfo?.claims,
      tokenIssuedAt: tokenInfo?.issuedAt,
      tokenExpiresAt: tokenInfo?.expiresAt,
    });
  }, [
    uid,
    isEmailVerified,
    isUidOptimistic,
    isAuthResolved,
    isSelfLoaded,
    isSelfSynced,
    isOrgResolved,
    isOrgConfirmed,
    syncedOrg,
    user?.role,
    user?.teams,
    activeOrg,
    myOrgs,
    tokenInfo,
  ]);

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      setIsAuthResolved(true);
      // The guess above is now settled — record the answer for the next visit
      writeLastUid(user?.uid ?? null);
      setIsUidOptimistic(false);

      if (user) {
        setUid(user.uid);
        setIsEmailVerified(!!user.emailVerified);
        setUserCreated(moment(user.metadata.creationTime));

        // Everything below invalidates data the listeners fetched under a
        // different identity, so it only applies when the identity actually
        // changed. Confirming a remembered uid changes nothing — blanking the
        // flags there would flash a spinner over data already read from cache.
        if (listenersUid.current !== user.uid) {
          // The tournaments listener is rebuilt on sign-in and every tournament
          // comes back with empty teams/games/standings until the subcollection
          // snapshots land, so consumers must not treat it as ready meanwhile.
          setIsTournamentsLoaded(false);
          // `teams` is left alone here: its query is organization-scoped now, so
          // the effect below blanks it whenever the organization changes — which
          // a sign-in always brings about.
        }

        listenersUid.current = user.uid;
      } else {
        // A switch caught mid-write belongs to the session being left. Dropping
        // it settles the caller — the modal would spin forever otherwise — and,
        // more to the point, releases the gate: the public listeners have to
        // come up for the signed-out visitor.
        setOrgSwitch(null);
        orgSwitchPromise.current?.reject(new Error('Signed out'));
        orgSwitchPromise.current = null;

        listenersUid.current = null;
        setUid(null);
        setIsEmailVerified(false);
        setUserCreated(null);
        setTournaments([]);
        // The tournament flags belong to the effect below: lowering them here
        // strands a visitor signed out already — that effect never re-runs
      }
    });

    return unsubAuth;
  }, []);

  // ---------------------------------------------------------------------------
  // Tournament subcollection registry
  //
  // Six listeners used to be opened for every active tournament in the
  // organization — hundreds of them against a client limit of 100, for the one
  // tournament somebody is actually looking at. Now a part is read only while a
  // mounted consumer holds it, and several consumers can hold the same one, so
  // the handle is kept alive by a counter rather than by whoever asked first.
  // Registered through `useTournamentParts` in utils/hooks.
  // ---------------------------------------------------------------------------

  // `${tournamentId}|${part}` → { count, unsub }. A ref, not state: acquiring is
  // not a render input, and the counters must survive the re-renders their own
  // snapshots cause.
  const tournPartRegistry = useRef(new Map());

  const patchTournamentPart = useCallback((tournamentId, patch) => {
    setTournParts((prev) => {
      const next = new Map(prev);
      next.set(tournamentId, { ...prev.get(tournamentId), ...patch });
      return next;
    });
  }, []);

  // Drops one part's data back to empty, and the whole entry once nothing of
  // that tournament is left — stale rows from a listener that is no longer
  // running would otherwise be indistinguishable from live ones.
  const dropTournamentPart = useCallback((tournamentId, part) => {
    setTournParts((prev) => {
      const parts = prev.get(tournamentId);
      if (!parts) return prev;

      const next = {
        ...parts,
        [part]: [],
        [`${part}Loaded`]: false,
        [`${part}Synced`]: false,
        [`${part}Failed`]: false,
      };
      const somethingLeft = tournamentParts.some(
        (name) => next[`${name}Loaded`],
      );

      const map = new Map(prev);
      if (somethingLeft) map.set(tournamentId, next);
      else map.delete(tournamentId);
      return map;
    });
  }, []);

  // Acquire the named parts of one tournament and get back the matching
  // release. Pairing them this way is what makes a refused acquire safe: it
  // hands back a release that undoes exactly what it did, so a counter can
  // never be left stranded above zero.
  const acquireTournamentParts = useCallback(
    (tournamentId, parts) => {
      // Mid-switch the listeners belong to the organization being left; the
      // write that lands would earn them a permission-denied. Nothing is opened
      // until the switch settles, and the identity of this callback changing is
      // what re-runs every consumer's effect afterwards.
      if (!tournamentId || isOrgSwitching) return () => {};

      parts.forEach((part) => {
        const key = `${tournamentId}|${part}`;
        const entry = tournPartRegistry.current.get(key) ?? {
          count: 0,
          unsub: null,
          skipped: false,
        };
        entry.count += 1;
        tournPartRegistry.current.set(key, entry);

        // A handle is dropped by the error path below, so "somebody already
        // holds this part" and "a listener is live" are separate questions —
        // the lesson block 5 paid for. Re-mounting a consumer reopens a part
        // that was refused earlier.
        if (entry.unsub || entry.skipped) return;

        // Visitors cannot read the private parts, nor non-managers referee pay
        // and roster: answered loaded and empty, or a waiting caller would hang
        if (
          (!uid && privateTournamentParts.has(part)) ||
          (!userManagesReferees && refereeManagerParts.has(part))
        ) {
          entry.skipped = true;
          patchTournamentPart(tournamentId, {
            [`${part}Loaded`]: true,
            [`${part}Synced`]: true,
          });
          return;
        }

        // Metadata changes carry the cache→server flip that raises Synced; a
        // fire with neither that flip nor changed rows is dropped
        let isPublished = false;
        let isSynced = false;
        entry.unsub = onSnapshot(
          collection(db, `tournaments/${tournamentId}/${part}`),
          { includeMetadataChanges: true },
          (snapshot) => {
            const hasRows = !isPublished || snapshot.docChanges().length > 0;
            const confirms = !isSynced && !snapshot.metadata.fromCache;
            if (!hasRows && !confirms) return;
            isPublished = true;
            if (confirms) isSynced = true;
            patchTournamentPart(tournamentId, {
              ...(hasRows && {
                [part]: readTournamentPart(part, snapshot),
                [`${part}Loaded`]: true,
              }),
              [`${part}Synced`]: isSynced,
              [`${part}Failed`]: false,
            });
          },
          (err) => {
            entry.unsub = null;
            reportListenerError(err, uid, {
              operation: 'listen',
              path: `tournaments/${tournamentId}/${part}`,
              message: `Failed to load tournament ${partLabels[part]}`,
              toastId: `tournament-${part}`,
              extra: { tournamentId },
            });
            // A refusal can arrive after the last holder let go, and marking a
            // released part "loaded" would put an entry back into the map that
            // nothing is reading any more
            if (tournPartRegistry.current.get(key) !== entry) return;
            // Otherwise the consumer waiting on the flag spins forever. Synced
            // stays down: a failed read proves nothing about what is absent
            patchTournamentPart(tournamentId, {
              [`${part}Loaded`]: true,
              [`${part}Failed`]: true,
            });
          },
        );
      });

      return () => {
        parts.forEach((part) => {
          const key = `${tournamentId}|${part}`;
          const entry = tournPartRegistry.current.get(key);
          if (!entry) return;

          entry.count -= 1;
          if (entry.count > 0) return;

          entry.unsub?.();
          tournPartRegistry.current.delete(key);
          dropTournamentPart(tournamentId, part);
        });
      };
    },
    [
      uid,
      userManagesReferees,
      isOrgSwitching,
      patchTournamentPart,
      dropTournamentPart,
      reportListenerError,
    ],
  );

  // ---------------------------------------------------------------------------
  // Entity registry
  //
  // Lets usersById/teamsById/tournamentsById answer `.get(id)` for a record
  // outside every other subscription. Chunked by 30 (the `in` limit) and
  // ref-counted; a released id stays subscribed for a while ("cold") so
  // navigating back is free. Registered through useUser/useTeam/useTournament.
  // ---------------------------------------------------------------------------

  // One bucket per kind. Refs, not state: chunk bookkeeping is not a render
  // input, and it must survive the re-renders its own snapshots cause.
  const entityRegistry = useRef({
    users: makeEntityBucket(),
    teams: makeEntityBucket(),
    tournaments: makeEntityBucket(),
  });

  // setState functions are stable, so these lookup tables are too — required
  // for the callbacks below that close over them to stay stable in turn.
  const entityOnDemandSetters = useMemo(
    () => ({
      users: setOnDemandUsers,
      teams: setOnDemandTeams,
      tournaments: setOnDemandTournaments,
    }),
    [],
  );
  const entityStatusSetters = useMemo(
    () => ({
      users: setUserStatusById,
      teams: setTeamStatusById,
      tournaments: setTournamentStatusById,
    }),
    [],
  );

  // Docs actually present in a snapshot, `active: false` filtered out — shared
  // by the status resolution and the merge below so the two never disagree on
  // what counts as present.
  const readPresentEntities = (kind, snapshot) => {
    const present = new Map();
    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();
      // `documentId() in [...]` cannot also filter on `active` without a new
      // composite index — filtered here instead, so a deleted team/tournament
      // resolves the same way it does through the full subscription: missing.
      if (entityActiveOnly[kind] && data.active === false) continue;
      present.set(docSnap.id, normalizeEntityDoc(kind, docSnap.id, data));
    }
    return present;
  };

  // Must run on every snapshot fire, never skipped by a docChanges() dedup: an
  // id that genuinely does not exist gets an empty cache snapshot followed by
  // an empty server one — no doc changes between them — and the cache→server
  // transition is exactly the event that is allowed to conclude 'missing'.
  const resolveEntityStatus = useCallback(
    (kind, requestedIds, snapshot) => {
      const present = readPresentEntities(kind, snapshot);
      entityStatusSetters[kind]((prev) => {
        const next = new Map(prev);
        for (const id of requestedIds) {
          if (present.has(id)) {
            next.set(id, 'present');
          } else if (snapshot.metadata.fromCache) {
            // A cache-only miss proves nothing — never conclude 'missing' here
            if (!next.has(id)) next.set(id, 'pending');
          } else {
            next.set(id, 'missing');
          }
        }
        return next;
      });
    },
    [entityStatusSetters],
  );

  // Safe to skip on a metadata-only re-fire: unlike status, the documents
  // themselves have not changed.
  const mergeEntityDocs = useCallback(
    (kind, requestedIds, snapshot) => {
      const present = readPresentEntities(kind, snapshot);
      entityOnDemandSetters[kind]((prev) => {
        let changed = present.size > 0;
        const next = new Map(prev);
        for (const [id, data] of present) next.set(id, data);
        // Deleted — or deactivated — since the last snapshot. Only a
        // server-confirmed absence may drop a record, the same rule that lets
        // resolveEntityStatus conclude 'missing'.
        if (!snapshot.metadata.fromCache) {
          for (const id of requestedIds) {
            if (!present.has(id) && next.delete(id)) changed = true;
          }
        }
        return changed ? next : prev;
      });
    },
    [entityOnDemandSetters],
  );

  const markEntitiesMissing = useCallback(
    (kind, ids) => {
      entityStatusSetters[kind]((prev) => {
        // An id already answered for keeps its answer: a refusal should release
        // the spinners, not turn a rendered page into "not found"
        const stale = ids.filter((id) => {
          const status = prev.get(id);
          return status !== 'present' && status !== 'missing';
        });
        // Re-marking what is already marked would hand every consumer of the
        // status map a new identity for nothing
        if (stale.length === 0) return prev;

        const next = new Map(prev);
        for (const id of stale) next.set(id, 'missing');
        return next;
      });
    },
    [entityStatusSetters],
  );

  const dropEntityOnDemand = useCallback(
    (kind, id) => {
      entityOnDemandSetters[kind]((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
      entityStatusSetters[kind]((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
    },
    [entityOnDemandSetters, entityStatusSetters],
  );

  const subscribeEntityChunk = useCallback(
    (bucket, kind, chunkId) => {
      // One flush can evict several ids from the same chunk, and the last one
      // out deletes it while its id is still in the caller's touched set
      const chunk = bucket.chunks.get(chunkId);
      if (!chunk) return;

      chunk.unsub?.();
      chunk.unsub = null;
      const ids = [...chunk.ids];
      if (ids.length === 0) {
        bucket.chunks.delete(chunkId);
        return;
      }

      // The rules let a caller list `users` only by their own organization, so
      // an id-only query is refused outright. Read off the bucket stamp — the
      // organization it was built for — rather than the current value, which a
      // switch can already have moved past.
      if (kind === 'users' && !bucket.stamp) return;
      const orgFilter =
        kind === 'users'
          ? [where('organizations', 'array-contains', bucket.stamp)]
          : [];

      let published = false;
      const unsub = onSnapshot(
        // No `active` filter here — readPresentEntities applies it client-side,
        // see entityActiveOnly.
        query(
          collection(db, entityCollectionName[kind]),
          where(documentId(), 'in', ids),
          ...orgFilter,
        ),
        { includeMetadataChanges: true },
        (snapshot) => {
          resolveEntityStatus(kind, ids, snapshot);

          if (published && snapshot.docChanges().length === 0) return;
          published = true;
          mergeEntityDocs(kind, ids, snapshot);
        },
        (err) => {
          reportListenerError(err, uid, {
            operation: 'listen',
            path: entityCollectionName[kind],
            filters: [
              `documentId() in [${ids.length} ids]`,
              ...(kind === 'users'
                ? [`organizations array-contains ${bucket.stamp}`]
                : []),
            ],
            orgScoped: kind === 'users',
            message: `Failed to load ${kind}`,
            toastId: `entities-${kind}`,
          });
          // Firestore ends a refused listen for good — cleared so the next
          // acquire of any of these ids reopens the chunk instead of skipping it
          if (chunk.unsub === unsub) chunk.unsub = null;
          markEntitiesMissing(kind, ids);
        },
      );
      chunk.unsub = unsub;
    },
    [
      resolveEntityStatus,
      mergeEntityDocs,
      markEntitiesMissing,
      reportListenerError,
      uid,
    ],
  );

  // Bookkeeping only — does not (re)subscribe. Callers batch several ids into
  // one chunk before opening a single listener for it; see acquireEntities.
  // Returns the touched chunk id, or null when the id already sits in a chunk
  // that is still listening — reopening that one would buy nothing.
  const assignEntityToChunk = useCallback((bucket, id) => {
    bucket.coldSet.delete(id);
    if (bucket.idToChunk.has(id)) {
      const chunkId = bucket.idToChunk.get(id);
      return bucket.chunks.get(chunkId)?.unsub ? null : chunkId;
    }

    let targetId = null;
    for (const [cid, chunk] of bucket.chunks) {
      if (chunk.ids.size < ENTITY_CHUNK_SIZE) {
        targetId = cid;
        break;
      }
    }
    if (targetId === null) {
      targetId = bucket.nextChunkId++;
      bucket.chunks.set(targetId, { ids: new Set(), unsub: null });
    }

    bucket.chunks.get(targetId).ids.add(id);
    bucket.idToChunk.set(id, targetId);
    return targetId;
  }, []);

  const maybeCompactEntityBucket = useCallback(
    (bucket, kind) => {
      const necessary = Math.max(
        1,
        Math.ceil(bucket.idToChunk.size / ENTITY_CHUNK_SIZE),
      );
      if (bucket.chunks.size <= necessary * ENTITY_COMPACT_RATIO) return;

      // A rare, deliberate reshuffle: every chunk in the bucket is torn down and
      // reopened minimally packed. The append-only assignment above pays this
      // resume-token cost only here, not on every id added or released.
      const allIds = [...bucket.idToChunk.keys()];
      for (const chunk of bucket.chunks.values()) chunk.unsub?.();
      bucket.chunks.clear();
      bucket.idToChunk.clear();
      bucket.nextChunkId = 0;
      const touched = new Set();
      for (const id of allIds) touched.add(assignEntityToChunk(bucket, id));
      for (const chunkId of touched)
        subscribeEntityChunk(bucket, kind, chunkId);
    },
    [assignEntityToChunk, subscribeEntityChunk],
  );

  // Bookkeeping only, like assignEntityToChunk — does not (re)subscribe.
  // Returns the touched chunk id (or null once the chunk itself is gone), so
  // flushEntityReleases can batch several evictions in the same chunk into
  // one resubscribe instead of one per id.
  const unassignEntityFromChunk = useCallback((bucket, id) => {
    const chunkId = bucket.idToChunk.get(id);
    if (chunkId == null) return null;

    const chunk = bucket.chunks.get(chunkId);
    chunk.ids.delete(id);
    bucket.idToChunk.delete(id);
    if (chunk.ids.size > 0) return chunkId;

    chunk.unsub?.();
    bucket.chunks.delete(chunkId);
    return null;
  }, []);

  const flushEntityReleases = useCallback(
    (bucket, kind) => {
      // An organization switch between the release and this microtask replaces
      // the bucket; evicting out of the old one would drop the new one's records
      if (entityRegistry.current[kind] !== bucket) return;

      for (const id of bucket.zeroSet) {
        bucket.coldSet.add(id);
        bucket.coldQueue.push(id);
      }
      bucket.zeroSet.clear();

      // An id released, re-acquired and released again leaves a stale duplicate
      // behind, and a queue longer than the ids it holds evicts still-cold ones
      // early. Rebuilt keeping the last occurrence, which is the most recent use.
      if (bucket.coldQueue.length > bucket.coldSet.size) {
        const seen = new Set();
        const compacted = [];
        for (let i = bucket.coldQueue.length - 1; i >= 0; i -= 1) {
          const id = bucket.coldQueue[i];
          if (seen.has(id) || !bucket.coldSet.has(id)) continue;
          seen.add(id);
          compacted.push(id);
        }
        compacted.reverse();
        bucket.coldQueue = compacted;
      }

      const touchedChunks = new Set();
      while (bucket.coldQueue.length > ENTITY_COLD_LRU_SIZE) {
        const evictId = bucket.coldQueue.shift();
        // Re-acquired since release — no longer cold, nothing to evict
        if (!bucket.coldSet.has(evictId)) continue;
        bucket.coldSet.delete(evictId);
        const chunkId = unassignEntityFromChunk(bucket, evictId);
        if (chunkId != null) touchedChunks.add(chunkId);
        dropEntityOnDemand(kind, evictId);
      }
      for (const chunkId of touchedChunks)
        subscribeEntityChunk(bucket, kind, chunkId);
      maybeCompactEntityBucket(bucket, kind);
    },
    [
      unassignEntityFromChunk,
      dropEntityOnDemand,
      subscribeEntityChunk,
      maybeCompactEntityBucket,
    ],
  );

  // 'users' is gated by the caller's own org (myOrg()), so any switch
  // invalidates it; 'teams'/'tournaments' are world-readable and only need
  // clearing on a full account change. Idempotent by design, keyed on a
  // stamp rather than triggered from one call site: a mounted consumer's own
  // re-acquire (acquireEntities changes identity on the same org/uid change
  // this reacts to) can otherwise run before the effect below, in the same
  // commit — child effects fire before parent effects — and register into a
  // bucket that effect then discards. Whichever of the two calls this first
  // performs the reset; the other sees the stamp already matches and no-ops.
  const ensureEntityBucketFresh = useCallback(
    (kind) => {
      const bucket = entityRegistry.current[kind];
      const stamp = kind === 'users' ? activeOrg : uid;
      if (bucket.stamp === stamp) return bucket;

      for (const chunk of bucket.chunks.values()) chunk.unsub?.();
      const fresh = makeEntityBucket();
      fresh.stamp = stamp;
      entityRegistry.current[kind] = fresh;
      entityOnDemandSetters[kind](new Map());
      entityStatusSetters[kind](new Map());
      return fresh;
    },
    [activeOrg, uid, entityOnDemandSetters, entityStatusSetters],
  );

  // Acquire a set of by-id records and get back the matching release. Release
  // is batched into a microtask: React unmounts the old route before mounting
  // the new one, and an id re-acquired in between must not flicker cold.
  const acquireEntities = useCallback(
    (kind, ids) => {
      if (!ids || ids.length === 0) return () => {};
      // 'users' is org-scoped by the rules: the query has to name the caller's
      // organization, and one the server has not confirmed yet earns a refusal.
      // Same gate the full users listener stands behind.
      if (kind === 'users' && (!uid || !isOrgConfirmed || !activeOrg)) {
        // A settled decline still has to answer, or `isResolved` never turns
        // true and the screen spins forever. A wait must not answer
        if (isUserResolved && (!uid || !activeOrg)) {
          // Stamped first, or the provider's own effect — which runs after every
          // consumer's in the same commit — clears the answer with the bucket
          ensureEntityBucketFresh(kind);
          markEntitiesMissing(kind, ids);
        }
        return () => {};
      }
      // Mid-switch the listeners belong to the organization being left
      if (isOrgSwitching) return () => {};

      const bucket = ensureEntityBucketFresh(kind);

      const touchedChunks = new Set();
      for (const id of ids) {
        bucket.zeroSet.delete(id);
        bucket.refCount.set(id, (bucket.refCount.get(id) || 0) + 1);
        const chunkId = assignEntityToChunk(bucket, id);
        if (chunkId != null) touchedChunks.add(chunkId);
      }
      for (const chunkId of touchedChunks)
        subscribeEntityChunk(bucket, kind, chunkId);
      maybeCompactEntityBucket(bucket, kind);

      return () => {
        for (const id of ids) {
          const n = (bucket.refCount.get(id) || 1) - 1;
          if (n > 0) {
            bucket.refCount.set(id, n);
            continue;
          }
          bucket.refCount.delete(id);
          bucket.zeroSet.add(id);
        }
        if (!bucket.flushScheduled) {
          bucket.flushScheduled = true;
          queueMicrotask(() => {
            bucket.flushScheduled = false;
            flushEntityReleases(bucket, kind);
          });
        }
      };
    },
    [
      uid,
      activeOrg,
      isOrgConfirmed,
      isOrgSwitching,
      isUserResolved,
      markEntitiesMissing,
      ensureEntityBucketFresh,
      assignEntityToChunk,
      subscribeEntityChunk,
      maybeCompactEntityBucket,
      flushEntityReleases,
    ],
  );

  // A parent's approved children, held for as long as they are signed in. This
  // is the one base set the app cannot derive from its own document: `myTeamIds`
  // above reads each child's `teams` back off these records, and the Live tab,
  // the tournament lists and the players list all hang off that. Registered
  // through the same registry as every other by-id read rather than as a
  // listener of its own — `acquireEntities` already handles chunking, the
  // organization switch and the release.
  useEffect(() => {
    if (!myChildIdsKey) return undefined;
    return acquireEntities('users', myChildIdsKey.split('|'));
  }, [myChildIdsKey, acquireEntities]);

  useEffect(() => {
    // Signed in but the self document has not landed yet, so the active
    // organization is still unknown. Querying now would either send an
    // undefined filter or none at all, and the rules refuse an unfiltered read.
    if (uid && !isOrgResolved) return;

    // A switch is being sent. Staying subscribed to the organization it leaves
    // would earn a permission-denied the moment the write lands, so the
    // listeners come down here and the write goes out after them. Nothing is
    // blanked yet — the lists still on screen belong to the organization the
    // user is still in until the write succeeds.
    if (isOrgSwitching) return;

    // A switch invalidates everything these listeners hold. Blank the flags so
    // consumers fall back to their spinners instead of showing the previous
    // organization's lists while the new snapshots are in flight.
    // `teams` is not blanked here: it is no longer organization-scoped but
    // keyed to whichever tournaments are open, and those consumers unmount on a
    // switch — which empties it through the effect that owns it.
    if (listenersOrg.current !== activeOrg) {
      setTournaments([]);
      setIsTournamentsLoaded(false);
      // Parent links live in their own effect but are organization-scoped
      // just the same
      setParentsByPlayerId(new Map());
      setIsParentsLoaded(false);
    }
    listenersOrg.current = activeOrg;
    // Idempotent — see ensureEntityBucketFresh. Always called, not gated on
    // the identity check above: 'teams'/'tournaments' key off uid, not org.
    ensureEntityBucketFresh('users');
    ensureEntityBucketFresh('teams');
    ensureEntityBucketFresh('tournaments');

    // A visitor reads no list: the public pages name one tournament and read
    // it by id. Unfiltered, the query below is every organization at once
    if (!uid) {
      setTournaments([]);
      setIsTournamentsLoaded(true);
      return undefined;
    }

    // Null only for a signed-in user who has no organization yet — a fresh
    // registration reading unfiltered, exactly as it did before.
    const orgFilter = activeOrg
      ? [where('organizations', 'array-contains', activeOrg)]
      : [];

    // Same constraints spelled out for the console — the SDK exposes no public
    // way to read a Query back, so the description is written alongside it
    const orgFilterLabel = activeOrg
      ? [`organizations array-contains ${activeOrg}`]
      : [];

    const unsubTournaments = onSnapshot(
      query(
        collection(db, 'tournaments'),
        where('active', '==', true),
        ...orgFilter,
      ),
      (snapshot) => {
        setIsTournamentsLoaded(true);
        setTournaments(snapToArr(snapshot).map(normalizeTournamentDoc));
      },
      (err) => {
        reportListenerError(err, uid, {
          operation: 'listen',
          path: 'tournaments',
          filters: ['active == true', ...orgFilterLabel],
          orgScoped: true,
          message: 'Failed to load tournaments',
        });
        setIsTournamentsLoaded(true);
      },
    );

    return unsubTournaments;
  }, [
    uid,
    activeOrg,
    isOrgResolved,
    isOrgSwitching,
    reportListenerError,
    ensureEntityBucketFresh,
  ]);

  // Which team documents anybody needs: the ones taking part in a tournament
  // somebody currently has open. Read off the part registry rather than off one
  // registered id, so it answers for a visitor and a signed-in caller alike —
  // the organization-wide query this replaced pulled every team in the database
  // to render the dozen a schedule names. Keyed on the joined ids rather than
  // the array, whose identity changes on every parts snapshot (P7). A
  // tournament whose `teams` part has not landed contributes nothing yet;
  // releasing that part blanks it (see dropTournamentPart), so a closed
  // tournament falls out of the key by itself.
  const openTeamIdsKey = useMemo(() => {
    const ids = new Set();
    for (const parts of tournParts.values()) {
      if (parts.teamsLoaded !== true) continue;
      for (const team of parts.teams || []) if (team.id) ids.add(team.id);
    }
    return [...ids].sort().join('|');
  }, [tournParts]);

  // Split from the effect above because its input — the team ids of the open
  // tournaments — is produced by the part listeners, and an effect cannot
  // depend on its own output.
  useEffect(() => {
    if (uid && !isOrgResolved) return undefined;
    if (isOrgSwitching) return undefined;

    setTeamsKey(openTeamIdsKey);

    if (!openTeamIdsKey) {
      setTeams([]);
      // Nothing is open, so there is nothing to read — an answer, not a wait.
      // Consumers that conclude from the absence of a team may act on it.
      setIsTeamsLoaded(true);
      setIsTeamsSynced(true);
      return undefined;
    }

    setIsTeamsLoaded(false);
    setIsTeamsSynced(false);

    // `in` takes at most 30 values, so a large tournament needs a few
    // listeners. They are merged by chunk index rather than replacing the
    // list, or whichever answered last would be the only one on screen.
    const ids = openTeamIdsKey.split('|');
    const chunks = [];
    for (let i = 0; i < ids.length; i += 30) chunks.push(ids.slice(i, i + 30));

    const byChunk = new Map();
    const loaded = new Set();
    const synced = new Set();

    const publish = () =>
      setTeams(chunks.flatMap((_, i) => byChunk.get(i) || []));

    const unsubs = chunks.map((chunk, i) =>
      onSnapshot(
        // No `active` filter: a deactivated team still plays out the schedule
        // it was admitted to, and dropping it would blank its name there.
        query(collection(db, 'teams'), where(documentId(), 'in', chunk)),
        { includeMetadataChanges: true },
        (snapshot) => {
          loaded.add(i);
          if (!snapshot.metadata.fromCache) synced.add(i);
          if (loaded.size === chunks.length) setIsTeamsLoaded(true);
          if (synced.size === chunks.length) setIsTeamsSynced(true);

          if (byChunk.has(i) && snapshot.docChanges().length === 0) return;

          byChunk.set(i, snapToArr(snapshot).map(normalizeTeamDoc));
          publish();
        },
        (err) => {
          reportListenerError(err, uid, {
            operation: 'listen',
            path: 'teams',
            filters: [`documentId() in [${chunk.length} ids]`],
            message: 'Failed to load teams',
            toastId: 'teams',
          });
          // Opened here too, or a refusal leaves every consumer spinning
          loaded.add(i);
          synced.add(i);
          if (loaded.size === chunks.length) setIsTeamsLoaded(true);
          if (synced.size === chunks.length) setIsTeamsSynced(true);
        },
      ),
    );

    return () => unsubs.forEach((unsub) => unsub());
  }, [uid, isOrgResolved, isOrgSwitching, openTeamIdsKey, reportListenerError]);

  // The flags belong to the key they were raised for. `openTeamIdsKey` is
  // computed during render and adopted by the effect only after it, so without
  // this a tournament whose teams subcollection has just landed reads as
  // "loaded, no teams" for the render in between — a sheet of TBD.
  const isTeamsKeyCurrent = teamsKey === openTeamIdsKey;
  const isTeamsReadable = isTeamsLoaded && isTeamsKeyCurrent;
  const isTeamsServerConfirmed = isTeamsSynced && isTeamsKeyCurrent;

  // There is no organization-wide `users` listener any more. It read ~10,000
  // documents on every cold start for every role, and nothing needed all of
  // them: a name on a public page travels denormalized on the team or game
  // document (`coachNames`, `refereeNames`), a record somebody opened by id
  // comes from the entity registry above, and the three screens that genuinely
  // want a list open their own narrow query — see `useOrgUsers` in utils/hooks.

  // The current user's own document — a single doc read, so signing in no
  // longer waits for the whole users collection. tourStatuses rides along
  // because only the current user's statuses are ever read (GuidedTour,
  // ParentCard); the per-user listeners this replaces were pure overhead.
  useEffect(() => {
    // This effect only re-runs when the identity changed, so whatever is held
    // here belongs to the previous one. Drop it before listening again —
    // keeping it would let the old profile drive roles and routing (and, since
    // the gate reads isSelfLoaded, do so with the app already rendered) until
    // the new snapshot lands.
    setSelfDoc(null);
    setSelfTourStatuses(null);
    setIsSelfLoaded(false);
    setIsSelfSynced(false);
    setSyncedOrg(null);

    if (!uid) return;

    // The cached snapshot and the server one that follows carry identical data,
    // so the body below is keyed off the content rather than off the event:
    // re-running it would hand every consumer of `self` a new object identity
    // for a document that did not change.
    let lastSelfJson = null;

    const unsubSelf = onSnapshot(
      doc(db, 'users', uid),
      // Without this the server confirmation is a metadata-only change and the
      // listener would never fire again, leaving isSelfSynced false forever.
      { includeMetadataChanges: true },
      (snapshot) => {
        if (!snapshot.metadata.fromCache) {
          setIsSelfSynced(true);
          // Read off the raw snapshot rather than off `selfDoc`: this is the one
          // organization value the rules are guaranteed to agree with, and the
          // gate for every organization-scoped query is built on it.
          setSyncedOrg(snapshot.data()?.organization ?? null);
        }

        const json = JSON.stringify(snapshot.data() ?? null);
        if (json === lastSelfJson) {
          setIsSelfLoaded(true);
          return;
        }

        lastSelfJson = json;

        if (snapshot.exists()) {
          setSelfDoc(normalizeUserDoc(snapshot.id, snapshot.data()));
        } else {
          setSelfDoc(null);
        }

        setIsSelfLoaded(true);
      },
      (err) => {
        reportListenerError(err, uid, {
          operation: 'listen doc',
          path: `users/${uid}`,
          message: 'Failed to load your profile',
        });
        // Never leave the app stuck behind the spinner on a read failure
        setIsSelfLoaded(true);
        setIsSelfSynced(true);
      },
    );

    const unsubTourStatuses = onSnapshot(
      collection(db, `users/${uid}/tourStatuses`),
      (snapshot) => setSelfTourStatuses(snapToArr(snapshot)),
      (err) => {
        reportListenerError(err, uid, {
          operation: 'listen',
          path: `users/${uid}/tourStatuses`,
          message: 'Failed to load tour progress',
        });
        // Never let a failed read silently disable every guided tour: unknown
        // progress means "nothing seen yet", so the worst case is a tour shown
        // twice rather than one that never appears again.
        setSelfTourStatuses([]);
      },
    );

    return () => {
      unsubSelf();
      unsubTourStatuses();
    };
  }, [uid, reportListenerError]);

  // Display names for the organizations the user belongs to. The rules allow
  // `get` only for those and close `list` outright, so this is a handful of
  // reads by id rather than a collection listener — and the documents barely
  // ever change, so a one-off read is enough.
  useEffect(() => {
    if (!myOrgs.length) {
      setOrgsById(new Map());
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const snaps = await Promise.all(
          myOrgs.map((id) => getDoc(doc(db, 'organizations', id))),
        );

        if (cancelled) return;

        setOrgsById(
          new Map(
            snaps
              .filter((snap) => snap.exists())
              .map((snap) => [snap.id, { id: snap.id, ...snap.data() }]),
          ),
        );
      } catch (err) {
        // Only costs the switcher its labels — it falls back to the ids
        logFirebaseError(err, {
          operation: 'getDoc',
          path: 'organizations/{id}',
          extra: { ids: myOrgs },
        });
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [myOrgs]);

  // Parent-player links for every player in one collection group listener
  // instead of one listener per player. Needs a rules block matching the
  // wildcard path `/{path=**}/parents/{parentId}` — a plain
  // `/users/{uid}/parents/{p}` rule does not apply to collection group queries.
  // That rule is organization-scoped now, and Firestore evaluates it against
  // every document a query would return: one document out of scope rejects the
  // whole query, so neither branch below may go out unfiltered.
  useEffect(() => {
    if (!uid) {
      setParentsByPlayerId(new Map());
      setIsParentsLoaded(false);
      return;
    }

    // The query shape depends on the role and on the active organization, and on
    // a cold load neither is known until the self document lands. Starting here
    // would send an unfiltered query and only narrow it a moment later — which
    // the rules now reject outright, toast and all, on every page load.
    if (!isOrgResolved) return;

    // Down while a switch is in flight, for the reason spelled out above
    if (isOrgSwitching) return;

    const isParent = user?.role === UsersRoles.PARENT;
    const isCoach = user?.role === UsersRoles.COACH;
    // Both of these read by uid, which no snapshot can invalidate
    const isSelfScoped = isParent || isCoach;

    // Only the organization branch below needs the server's word on which
    // organization that is. A parent or coach reads their own links by uid —
    // making them wait would cost a round trip and buy nothing.
    if (!isSelfScoped && !isOrgConfirmed) return;

    if (!isSelfScoped && !activeOrg) {
      // No organization to filter by (fresh registration): nothing to read, and
      // an unfiltered query would be refused
      setParentsByPlayerId(new Map());
      setIsParentsLoaded(true);
      return;
    }

    // Three shapes, one per rules branch — the collection-group rule matches a
    // query against exactly one of them and refuses anything else outright.
    // A parent reads their own links; a coach reads the links of the players on
    // their teams (`coachIds` is denormalized onto the link because a
    // collection-group query has no path to the player); an admin reads the
    // organization. Before this, a coach read the whole organization's links
    // and filtered them in the browser.
    const parentsQuery = isParent
      ? query(collectionGroup(db, 'parents'), where('parentId', '==', uid))
      : isCoach
        ? query(
            collectionGroup(db, 'parents'),
            where('coachIds', 'array-contains', uid),
          )
        : query(
            collectionGroup(db, 'parents'),
            where('organizations', 'array-contains', activeOrg),
          );

    const unsubParents = onSnapshot(
      parentsQuery,
      (snapshot) => {
        const map = new Map();

        snapshot.forEach((d) => {
          // users/{playerId}/parents/{parentId}
          const playerId = d.ref.parent.parent.id;
          if (!map.has(playerId)) map.set(playerId, []);
          map.get(playerId).push({ id: d.id, ...d.data() });
        });

        setParentsByPlayerId(map);
        setIsParentsLoaded(true);
      },
      (err) => {
        reportListenerError(err, uid, {
          operation: 'listen collectionGroup',
          path: 'parents',
          filters: [
            isParent
              ? `parentId == ${uid}`
              : isCoach
                ? `coachIds array-contains ${uid}`
                : `organizations array-contains ${activeOrg}`,
          ],
          // A parent or coach reads their own links regardless of organization
          orgScoped: !isSelfScoped,
          message: 'Failed to load parent links',
        });
        setIsParentsLoaded(true);
      },
    );

    return unsubParents;
    // Only the role and the organization shape the query — depending on `user`
    // would rebuild the listener on every self-document patch
  }, [
    uid,
    activeOrg,
    isOrgResolved,
    isOrgConfirmed,
    isOrgSwitching,
    user?.role,
    reportListenerError,
  ]);

  // Declared after every organization-scoped listener above on purpose: React
  // runs all of a commit's cleanups before any effect body, so by the time this
  // one fires those listeners are already unsubscribed and the write below has
  // nothing left for the rules to reject.
  useEffect(() => {
    if (!orgSwitch || orgSwitch.sent) return;

    const { organization } = orgSwitch;
    const pending = orgSwitchPromise.current;
    // Both branches below check the switch is still the one they sent: signing
    // out mid-write drops it, and that session's answer must not resurrect it
    const mine = orgSwitch;

    const send = async () => {
      try {
        await userSetOrganization({ organization });
        // The rules now answer to this organization, so the listeners come back
        // up on it right here rather than waiting for the self snapshot — a
        // switch cannot be left hanging on a snapshot that never arrives.
        setOrgSwitch((current) =>
          current === mine ? { organization, sent: true } : current,
        );
        pending?.resolve();
      } catch (error) {
        // The switch never happened — release the listeners onto the
        // organization the user is still in
        setOrgSwitch((current) => (current === mine ? null : current));
        pending?.reject(error);
      } finally {
        if (orgSwitchPromise.current === pending)
          orgSwitchPromise.current = null;
      }
    };

    send();
  }, [orgSwitch]);

  // The self document caught up: `activeOrg` reads the same value either way now
  useEffect(() => {
    if (orgSwitch?.sent && selfOrg === orgSwitch.organization)
      setOrgSwitch(null);
  }, [orgSwitch, selfOrg]);

  // ---------------------------------------------------------------------------
  // Chats
  //
  // Two listeners cover the whole surface, whatever the number of chats: the
  // list with its previews, and this member's read watermark in every one of
  // them. Neither counts messages — see the unread derivation below.
  //
  // No organization clause anywhere here, deliberately: membership is the
  // entire ACL, and `memberIds array-contains uid` is the one shape the `list`
  // rule can prove. A denial or a missing index is silent on purpose — until
  // the backend deploy lands the collection is not there at all, and a toast
  // on every page load would name a problem the visitor cannot act on.
  // ---------------------------------------------------------------------------

  useEffect(() => {
    // Nothing to read is an answer, not a wait
    if (!uid) {
      setChatDocs([]);
      setIsChatListLoaded(true);
      setIsChatListSynced(true);
      return undefined;
    }

    setIsChatListLoaded(false);
    setIsChatListSynced(false);

    let chatsPublished = false;

    return onSnapshot(
      query(
        collection(db, 'chats'),
        where('active', '==', true),
        where('memberIds', 'array-contains', uid),
        orderBy('lastMessageAt', 'desc'),
      ),
      { includeMetadataChanges: true },
      (snapshot) => {
        setIsChatListLoaded(true);
        if (!snapshot.metadata.fromCache) setIsChatListSynced(true);

        // Republishing on a metadata-only event would hand every consumer of
        // the context a new identity for nothing — the documents themselves
        // have not changed.
        if (chatsPublished && snapshot.docChanges().length === 0) return;
        chatsPublished = true;

        setChatDocs(snapToArr(snapshot));
      },
      (err) => {
        reportListenerError(err, uid, {
          operation: 'listen',
          path: 'chats',
          filters: [
            'active == true',
            `memberIds array-contains ${uid}`,
            'order by lastMessageAt desc',
          ],
          silent: true,
        });
        setIsChatListLoaded(true);
        setIsChatListSynced(true);
      },
    );
  }, [uid, reportListenerError]);

  // `uid` is a field as well as the document id: a collection group query has
  // no path back to the parent, and the filter on it is what proves the rule.
  // An absent member document is the honest state "never opened", not a
  // missing one — which is why nothing here waits for one to show up.
  useEffect(() => {
    if (!uid) {
      setChatMembers(new Map());
      setIsChatMembersLoaded(true);
      setIsChatMembersSynced(true);
      return undefined;
    }

    setIsChatMembersLoaded(false);
    setIsChatMembersSynced(false);

    let membersPublished = false;

    return onSnapshot(
      query(collectionGroup(db, 'members'), where('uid', '==', uid)),
      { includeMetadataChanges: true },
      (snapshot) => {
        setIsChatMembersLoaded(true);
        if (!snapshot.metadata.fromCache) setIsChatMembersSynced(true);

        if (membersPublished && snapshot.docChanges().length === 0) return;
        membersPublished = true;

        const next = new Map();
        snapshot.forEach((item) => {
          const chatId = item.ref.parent.parent?.id;
          if (chatId) next.set(chatId, item.data());
        });
        setChatMembers(next);
      },
      (err) => {
        reportListenerError(err, uid, {
          operation: 'listen collectionGroup',
          path: 'collectionGroup(members)',
          filters: [`uid == ${uid}`],
          silent: true,
        });
        setIsChatMembersLoaded(true);
        setIsChatMembersSynced(true);
      },
    );
  }, [uid, reportListenerError]);

  // Nothing counts unread messages, by design: a chat is unread when its last
  // message is newer than this member's watermark, and both values are already
  // in the two listeners above. Sending costs two writes whatever the size of
  // the chat, and there is no counter to drift.
  //
  // The two guards on `lastMessage` are what keep the timestamps honest. A chat
  // nobody has spoken in yet carries the `lastMessageAt` seeded at signup, so
  // that `orderBy` does not skip it, and the bare comparison would badge it for
  // good; and one's own message moves `lastMessageAt` past one's own watermark,
  // because marking read is what moves the watermark and sending is not
  // opening.
  const chats = useMemo(
    () =>
      chatDocs.map((chat) => {
        const lastReadAt = chatMembers.get(chat.id)?.lastReadAt ?? null;
        return {
          ...chat,
          lastReadAt,
          hasUnread:
            !!chat.lastMessage &&
            chat.lastMessage.senderId !== uid &&
            (chat.lastMessageAt?.toMillis() ?? 0) >
              (lastReadAt?.toMillis() ?? 0),
        };
      }),
    [chatDocs, chatMembers, uid],
  );

  // The badge is how many chats hold an unread message, which is also what
  // `push-getUnreadCount` returns off the same three terms — so the badge a
  // push writes and the one in the header agree.
  const unreadCount = useMemo(
    () => chats.filter((chat) => chat.hasUnread).length,
    [chats],
  );

  // Both listeners, not either: the list landing first would make every chat
  // look unread for a frame and flash the badge over an inbox that is read.
  const isChatsLoaded = isChatListLoaded && isChatMembersLoaded;
  const isChatsSynced = isChatListSynced && isChatMembersSynced;

  // Keep the app-icon badge in sync with the unread chats (covers app start,
  // real-time changes, and mark-as-read reset — the listeners fire on all).
  // Synced, not merely loaded: unread is concluded from the absence of a
  // watermark, and a cache that answers the list before the member documents
  // would badge every chat the visitor has already read.
  useEffect(() => {
    if (!isChatsSynced) return;
    updateAppBadge(unreadCount);
  }, [unreadCount, isChatsSynced]);

  // On each session: silently refresh the FCM token (tokens rotate) and listen
  // for foreground pushes. Badge is handled by the effect above. Registration
  // goes through a callable, so it waits for auth to confirm the identity —
  // firing it on a guessed uid would only send it without an auth context.
  useEffect(() => {
    if (!uid || isUidOptimistic) return;
    refreshTokenIfGranted().catch(() => {});
  }, [uid, isUidOptimistic]);

  // Once per app load and organization: the server asks Drive whether the
  // organization's sheet-sourced events changed. The answer lands on the
  // tournaments, where every listener sees the "Sheet changed" badge
  const activeOrganization = user?.organization;
  useEffect(() => {
    if (!uid || isUidOptimistic || !userManagesReferees || !activeOrganization)
      return;
    tournamentScheduleSourceCheck({}).catch(() => {});
  }, [uid, isUidOptimistic, userManagesReferees, activeOrganization]);

  const refreshEmailVerified = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return false;

    try {
      const was = !!user.emailVerified;
      await user.reload();

      const ok = !!user.emailVerified;
      setIsEmailVerified(ok);

      if (ok !== was) await user.getIdToken(true);
      return ok;
    } catch (e) {
      toast.error(e?.message ?? 'Failed to refresh email verification');
      return false;
    }
  }, []);

  // Memoized: an inline literal made every state change in the provider
  // re-render all ~70 useFirebase() consumers.
  const contextValue = useMemo(
    () => ({
      user,
      tournaments: tournamentsWithParts,
      usersById,
      teamsById,
      tournamentsById,
      standingsByKey,
      acquireTournamentParts,
      refereesById,
      acquireEntities,
      userStatusById,
      teamStatusById,
      tournamentStatusById,
      reportListenerError,
      parentsByPlayerId,
      childrenByParentId,
      getParentStatus,
      coachesByTeamId,
      myTeamIds,
      isMyTeamIdsResolved,
      activeOrg,
      myOrgs,
      orgsById,
      // Organization-scoped reads outside the provider wait on these too
      isOrgConfirmed,
      isOrgSwitching,
      setOrganization,
      chats,
      unreadCount,
      viewedChatRef,
      isChatsLoaded,
      isChatsSynced,
      isEmailVerified,
      refreshEmailVerified,
      userCreated,
      isUserResolved,
      // "data is available" — may be from cache, good enough to render
      isTeamsLoaded: isTeamsReadable,
      isTournamentsLoaded,
      isParentsLoaded,
      // "server confirmed" — required before concluding a record is missing
      isSelfSynced,
      isTeamsSynced: isTeamsServerConfirmed,
      userIsAdmin,
      userIsCoach,
      userIsPlayer,
      userIsFieldManager,
      userIsParent,
      userIsReferee,
      userIsRefereeAssigner,
      userManagesReferees,
    }),
    [
      user,
      tournamentsWithParts,
      usersById,
      teamsById,
      tournamentsById,
      standingsByKey,
      acquireTournamentParts,
      refereesById,
      acquireEntities,
      userStatusById,
      teamStatusById,
      tournamentStatusById,
      reportListenerError,
      parentsByPlayerId,
      childrenByParentId,
      getParentStatus,
      coachesByTeamId,
      myTeamIds,
      isMyTeamIdsResolved,
      activeOrg,
      myOrgs,
      orgsById,
      isOrgConfirmed,
      isOrgSwitching,
      setOrganization,
      chats,
      unreadCount,
      viewedChatRef,
      isChatsLoaded,
      isChatsSynced,
      isEmailVerified,
      refreshEmailVerified,
      userCreated,
      isUserResolved,
      isTeamsReadable,
      isTournamentsLoaded,
      isParentsLoaded,
      isSelfSynced,
      isTeamsServerConfirmed,
      userIsAdmin,
      userIsCoach,
      userIsPlayer,
      userIsFieldManager,
      userIsParent,
      userIsReferee,
      userIsRefereeAssigner,
      userManagesReferees,
    ],
  );

  return (
    <FirebaseContext.Provider value={contextValue}>
      {children}
    </FirebaseContext.Provider>
  );
}
